import {Button, Card, Col, Drawer, List, Row} from "antd";
import React, {useEffect, useState} from "react";
import axios from "axios";
import {httpRequest} from "../services/httpRequest";
import dayjs from "dayjs";
import {SurveyEditor} from "./SurveyEditor";
import {toast} from "react-toastify";
import {SurveySend} from "./SurveySend";
import {SurveyResults} from "./SurveyResults";
import {TakeSurvey} from "./TakeSurvey";

export const Surveys = ({history}) => {
    const [surveys, setSurveys] = useState([]);
    const [surveyId, setSurveyId] = useState(null);
    const [surveyRespondents, setSurveyRespondents] = useState([]);
    const [noRespondents, setNoRespondents] = useState(null);
    const [survey, setSurvey] = useState({} as any);
    const [loading, setLoading] = useState(false);
    const [results, setResults] = useState([]);
    const [previewSurveyId, setPreviewSurveyId] = useState(null as any);

    const downloadCsv = async (id: any) => {
        try {
            const response = await axios.get(`/api/v1/surveys/${id}/export`, {
                ...httpRequest.getHeaders(),
                responseType: 'blob'
            } as any);
            const blob = new Blob([response.data], {type: 'text/csv'});
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            // Try to read filename from content-disposition if present
            const cd = (response.headers && (response.headers['content-disposition'] || response.headers['Content-Disposition'])) as any;
            let filename = `survey-${id}-responses.csv`;
            if (cd && typeof cd === 'string') {
                const match = cd.match(/filename="?([^";]+)"?/);
                if (match && match[1]) filename = match[1];
            }
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            httpRequest.errorHandler(error, history);
        }
    }

    const createSurvey = () => {
        setLoading(true);
        const newSurvey = {name: 'New Survey', description: '', questions: []};
        axios.post(`/api/v1/surveys`, {survey: newSurvey}, httpRequest.getHeaders())
            .then((response: any) => {
                const created = response.data;
                toast.success('New survey created');
                setSurveys([...surveys || [], created]);
                setSurvey(created);
            })
            .catch(error => httpRequest.errorHandler(error, history))
            .finally(() => setLoading(false));
    }

    useEffect(() => {
        setLoading(true);
        axios.get(`/api/v1/surveys`,
            httpRequest.getHeaders())
            .then(response => {
                setSurveys(response.data);
            })
            .catch(error => httpRequest.errorHandler(error, history))
            .finally(() => setLoading(false));
    }, []);

    const editSurvey = (id: any) => {
        axios.get(`/api/v1/surveys/${id}`,
            httpRequest.getHeaders())
            .then(response => {
                setSurvey(response.data);
            })
            .catch(error => httpRequest.errorHandler(error, history));
    }

    const getSurveyRespondents = (id: any) => {
        setSurveyId(id);
        axios.get(`/api/v1/surveys/${id}/respondents`,
            httpRequest.getHeaders())
            .then(response => {
                setSurveyRespondents(response.data);
                if (!response.data.length) {
                    setNoRespondents({error: 'No users available to send to.'});
                }
            })
            .catch(error => httpRequest.errorHandler(error, history));
    }

    const save = () => {
        setLoading(true);
        axios.post(`/api/v1/surveys/${survey.id}`,
            {survey}, httpRequest.getHeaders())
            .then((response) => {
                toast.success('Survey has been successfully edited.');
            }).catch(error => httpRequest.errorHandler(error, history))
            .finally(() => setLoading(false));
    }

    const sendSurvey = () => {
        setLoading(true);
        axios.post(`/api/v1/surveys/${surveyId}/send`,
            {respondents: surveyRespondents.filter((respondent: any) => !respondent.disabled)}, httpRequest.getHeaders())
            .then((response: any) => {
                toast.success('Survey has been sent!');
                setSurveyRespondents([]);
                setSurveyId(null);
                setSurveys(surveys.map((survey: any) => survey.id === response.data.id ? {
                    ...survey,
                    last_sent: response.data.last_sent
                } : survey));
            }).catch(error => httpRequest.errorHandler(error, history))
            .finally(() => setLoading(false));
    }

    useEffect(() => {
        if (noRespondents) {
            toast.error(noRespondents.error);
        }
    }, [noRespondents]);

    return (
        <Col xs={24} className={'mt-3'}>
            <Card title={'Youth Surveys'}
                  extra={<Button disabled={loading} onClick={createSurvey} type={'primary'}>Create New Survey</Button>}>
                <List dataSource={surveys}
                      loading={loading}
                      renderItem={(item, index) => (
                          <List.Item key={index} actions={[<Button onClick={() => getSurveyRespondents(item.id)}
                                                                   disabled={item.last_sent && dayjs(item.last_sent).isAfter(dayjs().startOf('day'))}
                                                                   type={'primary'}>Send</Button>,
                              <Button disabled={item.last_sent} onClick={() => editSurvey(item.id)}>Edit</Button>,
                              <Button onClick={() => setPreviewSurveyId(item.id)}>Preview</Button>,
                              <Button disabled={!item.responses.length} onClick={() => setResults(item.responses)}
                                      type={'primary'} ghost>Results</Button>,
                              <Button disabled={!item.responses.length} onClick={() => downloadCsv(item.id)}>Download
                                  CSV</Button>]}>
                              <List.Item.Meta
                                  title={item.name}
                                  description={item.description}
                              />
                              <Row>Last
                                  sent: {item.last_sent ? dayjs(item.last_sent).format('MMM DD, YYYY') : 'Never'}</Row>
                          </List.Item>
                      )}
                />
                <SurveyResults results={results}/>
            </Card>
            <Drawer title={'Edit Survey Questions'} width={'80%'} onClose={() => setSurvey({})} open={survey.id}>
                <SurveyEditor survey={survey} setSurvey={setSurvey}/>
                <Button disabled={loading} onClick={() => save()} type={'primary'}>Save</Button>
            </Drawer>
            <Drawer title={'Send Survey'} width={'50%'} onClose={() => setSurveyRespondents([])}
                    open={surveyRespondents.length > 0}>
                <SurveySend surveyId={surveyId} respondents={surveyRespondents}
                            setSurveyRespondents={setSurveyRespondents}/>
                <Button className={'mt-3'}
                        disabled={loading || surveyRespondents.filter((resp: any) => !resp.disabled).length < 1}
                        onClick={() => sendSurvey()}
                        type={'primary'}>Send</Button>
            </Drawer>
            <Drawer width={'80%'} open={!!previewSurveyId} onClose={() => setPreviewSurveyId(null)}
                    key={previewSurveyId}>
                <TakeSurvey match={{params: {id: previewSurveyId, isAdmin: true}}}/>
            </Drawer>
        </Col>
    )
}